import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase, isSupabaseReady } from '@/integrations/supabase/client';
import { getSubdomain } from '@/lib/utils';

export interface TenantConfig {
  id: string;
  name: string;
  subdomain: string;
  logoUrl?: string;
  colorScheme?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    background?: string;
    text?: string;
    // New optional fields for quick tenant theming
    navbarBg?: string;
    navbarText?: string;
    navbarHoverText?: string; // optional hover/active color
    startMatrixButtonColor?: string;
    getQuotesButtonColor?: string;
  };
  bannerConfig?: {
    title?: string;
    subtitle?: string;
    backgroundImage?: string;
    ctaText?: string;
    ctaLink?: string;
    backgroundColor?: string;
    textColor?: string;
    // Bottom-of-homepage banner quick settings
    bottomBannerBackgroundColor?: string;
    bottomBannerTextColor?: string;
  };
}

interface TenantContextType {
  tenant: TenantConfig | null;
  loading: boolean;
  error: string | null;
  refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenant] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  const getCurrentSubdomain = useCallback((): string => getSubdomain(), [location.pathname]);

  // Development-only tenant override for localhost subdomains
  // Always enable for dubaicommercity on *.localhost to simplify dev, regardless of Supabase readiness
  const getDevTenantOverride = useCallback((subdomain: string): TenantConfig | null => {
    if (typeof window === 'undefined') return null;
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
    if (!isLocal) return null;

    if (subdomain === 'dubaicommercity') {
      // Use public-root URLs for /Logos and /backgrounds so they resolve in dev, preview and prod
      return {
        id: `dev-${subdomain}`,
        name: 'Dubai CommerCity',
        subdomain,
        logoUrl: 'https://mborlyertmbornltlmdf.supabase.co/storage/v1/object/public/insurer-logos/1650863576898.jpeg',
        bannerConfig: {
          backgroundImage: 'https://mborlyertmbornltlmdf.supabase.co/storage/v1/object/public/insurer-logos/dubai-commercity.jpg',
          backgroundColor: 'transparent',
          textColor: '#FFFFFF',
        },
      };
    }

    return null;
  }, []);
  const fetchTenant = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const subdomain = getCurrentSubdomain();

      // Immediate localhost override only when Supabase is not configured
      const possibleDev = getDevTenantOverride(subdomain);
      if (possibleDev) {
        console.log(`[Tenant] Dev override engaged for subdomain: ${subdomain}`);
        if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost'))) {
          // eslint-disable-next-line no-console
          console.debug('[Tenant] dev override tenant', possibleDev);
        }
        setTenant(possibleDev);
        setLoading(false);
        return;
      }
      const { data, error: fetchError } = await supabase
        .from('tenants')
        .select('*')
        .eq('subdomain', subdomain)
        .maybeSingle();

      if (fetchError) {
        // Unexpected error from Supabase
        setError(fetchError.message);
        setTenant(null);
        return;
      }

      if (!data) {
        // No tenant found for this subdomain - this is OK for localhost development
        console.log(`No tenant found for subdomain: ${subdomain} - using default configuration`);
        setError(null);
        setTenant(null);
        return;
      }

      const tenantConfig: TenantConfig = {
        id: data.id,
        name: data.name,
        subdomain: data.subdomain,
        logoUrl: data.logo_url || undefined,
        colorScheme: data.color_scheme_json ? (typeof data.color_scheme_json === 'string' ? JSON.parse(data.color_scheme_json) : data.color_scheme_json) : undefined,
        bannerConfig: data.banner_config_json ? (typeof data.banner_config_json === 'string' ? JSON.parse(data.banner_config_json) : data.banner_config_json) : undefined,
      };

      // Merge in dev override (localhost) visuals only; preserve DB identity
      const currentSub = getCurrentSubdomain();
      const devOverride = getDevTenantOverride(currentSub);
      let finalTenant: TenantConfig = devOverride
        ? {
            ...tenantConfig,
            logoUrl: devOverride.logoUrl ?? tenantConfig.logoUrl,
            bannerConfig: {
              ...(tenantConfig.bannerConfig || {}),
              ...(devOverride.bannerConfig || {}),
            },
          }
        : tenantConfig;

	      // Production-safe fallback for DCC: ensure logo and banner are set even if DB fields are empty/blank
	      if (finalTenant.subdomain === 'dubaicommercity') {
	        const hasLogo = typeof finalTenant.logoUrl === 'string' && finalTenant.logoUrl.trim().length > 0;
	        const hasBg = typeof finalTenant.bannerConfig?.backgroundImage === 'string' && (finalTenant.bannerConfig!.backgroundImage as string).trim().length > 0;
	        finalTenant = {
	          ...finalTenant,
	          logoUrl: hasLogo ? finalTenant.logoUrl : 'https://mborlyertmbornltlmdf.supabase.co/storage/v1/object/public/insurer-logos/1650863576898.jpeg',
	          bannerConfig: {
	            ...(finalTenant.bannerConfig || {}),
	            backgroundImage: hasBg ? finalTenant.bannerConfig!.backgroundImage : 'https://mborlyertmbornltlmdf.supabase.co/storage/v1/object/public/insurer-logos/dubai-commercity.jpg',
	            backgroundColor: finalTenant.bannerConfig?.backgroundColor ?? 'transparent',
	            textColor: finalTenant.bannerConfig?.textColor ?? '#FFFFFF',
	          },
	        };
	      }

	      // Hard override for Incorpify hero/nav background to match live design
	      if (finalTenant.subdomain === 'incorpify' || finalTenant.subdomain === 'incorpify-ai') {
	        finalTenant = {
	          ...finalTenant,
	          bannerConfig: {
	            ...(finalTenant.bannerConfig || {}),
	            // Ensure a solid dark background and readable white text for hero + nav
	            backgroundColor: '#0a0a0a',
	            textColor: finalTenant.bannerConfig?.textColor ?? '#FFFFFF',
	          },
	        };
	      }

	      // Dev-only: log the final tenant object so we can confirm banner/ logo URLs
	      if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost'))) {
	        // eslint-disable-next-line no-console
	        console.debug('[Tenant] final tenant', finalTenant);
	      }

	      setTenant(finalTenant);

      // Apply color scheme to CSS variables if available
      if (tenantConfig.colorScheme) {
        applyColorScheme(tenantConfig.colorScheme);
      } else {
        console.log('No color scheme found in tenant config');
      }

    } catch (err) {
      console.warn('Tenant fetch error (this is normal in development):', err);
      // On error, try dev override for localhost only when Supabase is not configured
      if (!isSupabaseReady) {
        const subdomain = getCurrentSubdomain();
        const devOverride = getDevTenantOverride(subdomain);
        if (devOverride) {
          console.log(`[Tenant] Using dev override after error for subdomain: ${subdomain}`);
          setTenant(devOverride);
          return;
        }
      }
      // Don't set error in development mode - just log it
      if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        setError(err instanceof Error ? err.message : 'Failed to fetch tenant configuration');
      }
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, [getCurrentSubdomain, getDevTenantOverride]);

  const applyColorScheme = (colorScheme: TenantConfig['colorScheme']) => {
    if (!colorScheme) return;
    const root = document.documentElement;

    if (colorScheme.primary) {
      root.style.setProperty('--tenant-primary', colorScheme.primary);
      console.log('Set --tenant-primary to:', colorScheme.primary);
    }
    if (colorScheme.secondary) {
      root.style.setProperty('--tenant-secondary', colorScheme.secondary);
      console.log('Set --tenant-secondary to:', colorScheme.secondary);
    }
    if (colorScheme.accent) {
      root.style.setProperty('--tenant-accent', colorScheme.accent);
    }
    if (colorScheme.background) {
      root.style.setProperty('--tenant-background', colorScheme.background);
    }
    if (colorScheme.text) {
      root.style.setProperty('--tenant-text', colorScheme.text);
    }
  };

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  const refreshTenant = async (): Promise<void> => {
    await fetchTenant();
  };

  const value: TenantContextType = {
    tenant,
    loading,
    error,
    refreshTenant,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};
